import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  adminApi,
  AccountRequisites,
  SaveAccountRequisitesRequest,
} from '../api/admin';
import { getErrorMessage } from '../api/client';
import { notifySuccess } from '../utils/notify';
import { validateBankingIdentifiers } from '../utils/bankingValidation';
import {
  ibanCountryCode,
  isValidBic,
  isValidIbanChecksum,
  normalizeIban,
} from '../utils/sepaIban';

type PaymentMethod = 'SWIFT' | 'SEPA';

const EMPTY_FORM: SaveAccountRequisitesRequest = {
  paymentMethod: 'SWIFT',
  accountName: '',
  accountNumber: '',
  iban: '',
  swiftCode: '',
  bankName: '',
  bankLocation: '',
  bankAddress: '',
  branchCode: '',
  bankCode: '',
  intermediaryBankName: '',
  intermediaryBankSwift: '',
  status: 'ACTIVE',
  isPublished: true,
  isPrimary: true,
  notes: '',
};

function trim(value?: string | null): string {
  return value?.trim() || '';
}

function toPayload(form: SaveAccountRequisitesRequest, includeMethod: boolean): SaveAccountRequisitesRequest {
  const method = (form.paymentMethod || 'SWIFT').toUpperCase() as PaymentMethod;
  const payload: SaveAccountRequisitesRequest = {
    accountName: trim(form.accountName) || undefined,
    accountNumber: trim(form.accountNumber) || undefined,
    iban: trim(form.iban) || undefined,
    swiftCode: trim(form.swiftCode) || undefined,
    bankName: trim(form.bankName) || undefined,
    bankLocation: trim(form.bankLocation) || undefined,
    bankAddress: trim(form.bankAddress) || undefined,
    isPrimary: form.isPrimary ?? true,
    notes: trim(form.notes) || undefined,
  };

  if (includeMethod) payload.paymentMethod = method;
  if (includeMethod) {
    payload.status = form.status || 'PENDING';
    payload.isPublished = !!form.isPublished;
  }
  if (method === 'SWIFT') {
    payload.branchCode = trim(form.branchCode) || undefined;
    payload.bankCode = trim(form.bankCode) || undefined;
    payload.intermediaryBankName = trim(form.intermediaryBankName) || undefined;
    payload.intermediaryBankSwift = trim(form.intermediaryBankSwift) || undefined;
  }

  return payload;
}

function paymentMethodOf(value?: string | null): PaymentMethod {
  return value?.trim().toUpperCase() === 'SEPA' ? 'SEPA' : 'SWIFT';
}

function isActiveStatus(value?: string | null): boolean {
  return value?.trim().toUpperCase() === 'ACTIVE';
}

function AccountRequisitesPanel({
  accountId,
  allowSepa = false,
  hasLegacySwiftDetails = false,
}: {
  accountId: string;
  allowSepa?: boolean;
  hasLegacySwiftDetails?: boolean;
}) {
  const [items, setItems] = useState<AccountRequisites[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<SaveAccountRequisitesRequest>(EMPTY_FORM);
  const [sepaCountryCodes, setSepaCountryCodes] = useState<Set<string>>(new Set());
  const [sepaCountriesReady, setSepaCountriesReady] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await adminApi.getAccountRequisites(accountId, false);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load account requisites'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void load();
    setFormOpen(false);
    setEditingId(null);
  }, [accountId, load]);

  useEffect(() => {
    if (!allowSepa) {
      setSepaCountryCodes(new Set());
      setSepaCountriesReady(false);
      return;
    }

    let cancelled = false;
    void adminApi.getSepaCountries(true)
      .then((res) => {
        if (cancelled) return;
        const codes = new Set(
          (res.data || [])
            .map((item) => (item.ibanCountryCode || '').trim().toUpperCase())
            .filter(Boolean),
        );
        setSepaCountryCodes(codes);
        setSepaCountriesReady(codes.size > 0);
      })
      .catch(() => {
        if (cancelled) return;
        setSepaCountryCodes(new Set());
        setSepaCountriesReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allowSepa]);

  const active = useMemo(
    () => items.filter((item) => isActiveStatus(item.status)),
    [items],
  );
  const hasSwift = hasLegacySwiftDetails || active.some((item) => paymentMethodOf(item.paymentMethod) === 'SWIFT');
  const hasSepa = active.some((item) => paymentMethodOf(item.paymentMethod) === 'SEPA');
  const method = paymentMethodOf(form.paymentMethod);

  const openCreate = (paymentMethod: PaymentMethod) => {
    if (paymentMethod === 'SEPA' && !allowSepa) return;
    setEditingId(null);
    setForm({ ...EMPTY_FORM, paymentMethod, isPrimary: active.length === 0 });
    setFormOpen(true);
    setError('');
  };

  const openEdit = (item: AccountRequisites) => {
    setEditingId(item.id);
    setForm({
      paymentMethod: item.paymentMethod,
      accountName: item.accountName || '',
      accountNumber: item.accountNumber || '',
      iban: item.iban || '',
      swiftCode: item.swiftCode || '',
      bankName: item.bankName || '',
      bankLocation: item.bankLocation || '',
      bankAddress: item.bankAddress || '',
      branchCode: item.branchCode || '',
      bankCode: item.bankCode || '',
      intermediaryBankName: item.intermediaryBankName || '',
      intermediaryBankSwift: item.intermediaryBankSwift || '',
      status: item.status,
      isPublished: item.isPublished,
      isPrimary: item.isPrimary,
      notes: item.notes || '',
    });
    setFormOpen(true);
    setError('');
  };

  const save = async () => {
    const payload = toPayload(form, !editingId);
    if (method === 'SEPA') {
      if (!trim(payload.iban) || !trim(payload.swiftCode)) {
        setError('IBAN and BIC are required for SEPA requisites.');
        return;
      }
      const iban = normalizeIban(payload.iban);
      if (!isValidIbanChecksum(iban)) {
        setError('IBAN format or checksum is invalid.');
        return;
      }
      if (!sepaCountriesReady || sepaCountryCodes.size === 0) {
        setError('SEPA country list is unavailable. Refresh and try again.');
        return;
      }
      if (!sepaCountryCodes.has(ibanCountryCode(iban))) {
        setError('This IBAN country is not in the SEPA zone.');
        return;
      }
      if (!isValidBic(payload.swiftCode)) {
        setError('BIC must contain 8 or 11 ISO 9362 characters.');
        return;
      }
      payload.iban = iban;
      payload.swiftCode = trim(payload.swiftCode).toUpperCase();
    } else {
      const identifierError = validateBankingIdentifiers(payload.accountNumber, payload.iban);
      if (identifierError) {
        setError(identifierError);
        return;
      }
    }

    try {
      setSaving(true);
      setError('');
      if (editingId) {
        await adminApi.updateAccountRequisites(editingId, payload);
        notifySuccess('Requisites updated.');
      } else {
        await adminApi.createAccountRequisites(accountId, payload);
        notifySuccess('Requisites created.');
      }
      setFormOpen(false);
      setEditingId(null);
      await load();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to save requisites'));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (item: AccountRequisites, status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'CLOSED') => {
    const reason = status === 'SUSPENDED' || status === 'CLOSED'
      ? window.prompt(`Reason for ${status.toLowerCase()} requisites`) ?? undefined
      : undefined;
    if ((status === 'SUSPENDED' || status === 'CLOSED') && !trim(reason)) return;

    try {
      setSaving(true);
      setError('');
      await adminApi.updateAccountRequisitesStatus(item.id, status, trim(reason) || undefined);
      notifySuccess(`Requisites status changed to ${status}.`);
      await load();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to change requisites status'));
    } finally {
      setSaving(false);
    }
  };

  const setPublished = async (item: AccountRequisites, isPublished: boolean) => {
    try {
      setSaving(true);
      setError('');
      await adminApi.setAccountRequisitesPublication(item.id, isPublished);
      notifySuccess(isPublished ? 'Requisites published.' : 'Requisites hidden.');
      await load();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to change requisites visibility'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3">
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Account Requisites</h4>
        <div className="flex gap-2">
          {!hasSwift && (
            <button
              type="button"
              onClick={() => openCreate('SWIFT')}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Add SWIFT
            </button>
          )}
          {allowSepa && !hasSepa && (
            <button
              type="button"
              onClick={() => openCreate('SEPA')}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              Add SEPA
            </button>
          )}
        </div>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading requisites…</p>}
      {error && !formOpen && (
        <p className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}

      {!loading && items.length === 0 && !formOpen && (
        <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
          {hasLegacySwiftDetails
            ? 'Existing banking details are treated as SWIFT requisites and will be available here after the data backfill.'
            : allowSepa
              ? 'No payment requisites yet. Add SWIFT or SEPA details to issue account numbers and IBANs.'
              : 'No SWIFT requisites yet. Add SWIFT details to issue account numbers and IBANs.'}
        </p>
      )}

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{paymentMethodOf(item.paymentMethod)}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  isActiveStatus(item.status) ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                }`}>
                  {item.status}
                </span>
                {item.isPrimary && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Primary
                  </span>
                )}
                {item.isPublished ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                    Published
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                    Hidden
                  </span>
                )}
              </div>
              {item.status !== 'CLOSED' && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    Edit
                  </button>
                  {isActiveStatus(item.status) && (
                    <>
                      <button type="button" onClick={() => void setPublished(item, !item.isPublished)} className="text-xs font-medium text-teal-700 hover:text-teal-900">
                        {item.isPublished ? 'Hide' : 'Publish'}
                      </button>
                      <button type="button" onClick={() => void changeStatus(item, 'SUSPENDED')} className="text-xs font-medium text-amber-700 hover:text-amber-900">
                        Suspend
                      </button>
                    </>
                  )}
                  {item.status === 'PENDING' && (
                    <button type="button" onClick={() => void changeStatus(item, 'ACTIVE')} className="text-xs font-medium text-green-700 hover:text-green-900">
                      Activate
                    </button>
                  )}
                  {item.status === 'SUSPENDED' && (
                    <button type="button" onClick={() => void changeStatus(item, 'ACTIVE')} className="text-xs font-medium text-green-700 hover:text-green-900">
                      Activate
                    </button>
                  )}
                  <button type="button" onClick={() => void changeStatus(item, 'CLOSED')} className="text-xs font-medium text-red-600 hover:text-red-800">
                    Close
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <RequisiteRow label="Account name" value={item.accountName} />
              <RequisiteRow label="Account number" value={item.accountNumber} mono />
              <RequisiteRow label="IBAN" value={item.iban} mono />
              <RequisiteRow label={paymentMethodOf(item.paymentMethod) === 'SEPA' ? 'BIC' : 'SWIFT'} value={item.swiftCode} mono />
              <RequisiteRow label="Bank name" value={item.bankName} />
              <RequisiteRow label="Bank location" value={item.bankLocation} />
              {paymentMethodOf(item.paymentMethod) === 'SWIFT' && (
                <>
                  <RequisiteRow label="Branch code" value={item.branchCode} />
                  <RequisiteRow label="Bank code" value={item.bankCode} />
                  <RequisiteRow label="Intermediary bank" value={item.intermediaryBankName} />
                  <RequisiteRow label="Intermediary SWIFT" value={item.intermediaryBankSwift} mono />
                </>
              )}
              <div className="sm:col-span-2">
                <RequisiteRow label="Bank address" value={item.bankAddress} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {formOpen && (
        <div className="mt-3 border border-indigo-100 bg-white rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-semibold text-gray-900">
              {editingId ? `Edit ${method} requisites` : `New ${method} requisites`}
            </h5>
            <button type="button" onClick={() => setFormOpen(false)} className="text-xs text-gray-500 hover:text-gray-700">
              Close
            </button>
          </div>
          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Account name">
              <input value={form.accountName || ''} onChange={(e) => setForm((f) => ({ ...f, accountName: e.target.value }))} className={fieldClass} />
            </Field>
            {method === 'SWIFT' && (
              <Field label="Account number">
                <input value={form.accountNumber || ''} onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))} className={`${fieldClass} font-mono`} />
              </Field>
            )}
            <Field label="IBAN" required={method === 'SEPA'}>
              <input value={form.iban || ''} onChange={(e) => setForm((f) => ({ ...f, iban: e.target.value }))} className={`${fieldClass} font-mono`} />
            </Field>
            <Field label={method === 'SEPA' ? 'BIC' : 'SWIFT'} required={method === 'SEPA'}>
              <input value={form.swiftCode || ''} onChange={(e) => setForm((f) => ({ ...f, swiftCode: e.target.value }))} className={`${fieldClass} font-mono`} />
            </Field>
            <Field label="Bank name">
              <input value={form.bankName || ''} onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))} className={fieldClass} />
            </Field>
            <Field label="Bank location">
              <input value={form.bankLocation || ''} onChange={(e) => setForm((f) => ({ ...f, bankLocation: e.target.value }))} className={fieldClass} />
            </Field>
            {method === 'SWIFT' && (
              <>
                <Field label="Branch code">
                  <input value={form.branchCode || ''} onChange={(e) => setForm((f) => ({ ...f, branchCode: e.target.value }))} className={fieldClass} />
                </Field>
                <Field label="Bank code">
                  <input value={form.bankCode || ''} onChange={(e) => setForm((f) => ({ ...f, bankCode: e.target.value }))} className={fieldClass} />
                </Field>
                <Field label="Intermediary bank">
                  <input value={form.intermediaryBankName || ''} onChange={(e) => setForm((f) => ({ ...f, intermediaryBankName: e.target.value }))} className={fieldClass} />
                </Field>
                <Field label="Intermediary SWIFT">
                  <input value={form.intermediaryBankSwift || ''} onChange={(e) => setForm((f) => ({ ...f, intermediaryBankSwift: e.target.value }))} className={`${fieldClass} font-mono`} />
                </Field>
              </>
            )}
            <div className="sm:col-span-2">
              <Field label="Bank address">
                <input value={form.bankAddress || ''} onChange={(e) => setForm((f) => ({ ...f, bankAddress: e.target.value }))} className={fieldClass} />
              </Field>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={!!form.isPrimary}
              onChange={(e) => setForm((f) => ({ ...f, isPrimary: e.target.checked }))}
            />
            Primary requisites
          </label>
          {!editingId && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Lifecycle status">
                <select value={form.status || 'PENDING'} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as SaveAccountRequisitesRequest['status'] }))} className={fieldClass}>
                  <option value="PENDING">PENDING</option>
                  <option value="ACTIVE">ACTIVE</option>
                </select>
              </Field>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 pt-6">
                <input
                  type="checkbox"
                  checked={!!form.isPublished}
                  disabled={form.status !== 'ACTIVE'}
                  onChange={(e) => setForm((f) => ({ ...f, isPublished: e.target.checked }))}
                />
                Publish for client
              </label>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setFormOpen(false)} className="px-3 py-2 text-sm text-gray-600">
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void save()}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const fieldClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

function RequisiteRow({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  const text = value?.trim();
  if (!text) return null;
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <p className={`text-sm text-gray-800 ${mono ? 'font-mono break-all' : ''}`}>{text}</p>
    </div>
  );
}

export default AccountRequisitesPanel;
